import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FilterFieldValue, FilterDateRangeValue } from '../covance-filter.models';
import { CovanceFilterDateType } from './covance-filter-date-type';
import * as moment from 'moment';

@Injectable()
export class CovanceFilterDateRangeType extends CovanceFilterDateType {
    private readonly separator = 'To';
    private readonly dateBeginRegExp = new RegExp(`^(\\d{4}-\\d{2}-\\d{2})${this.separator}`);
    private readonly dateEndRegExp = new RegExp(`${this.separator}(\\d{4}-\\d{2}-\\d{2})$`);
    private dynamicRanges: { [key: string]: () => FilterDateRangeValue } = {
        'today': () => this.getTodayRange(),
        'monthToDate': () => this.getMonthToDateRange(),
        'yearToDate': () => this.getYearToDateRange()
    };

    constructor(router: Router, route: ActivatedRoute) {
        super(router, route);
    }

    public serializeValue(filterValue: any): string {
        const valueRange = filterValue as FilterDateRangeValue;
        if (valueRange == null) {
            return '';
        }
        switch (valueRange.rangeType) {
            case 'today': return 'Today';
            case 'monthToDate': return 'Month To Date';
            case 'yearToDate': return 'Year To Date';
            case 'dateRange': return `From ${valueRange.dateBegin || ' - '} To ${valueRange.dateEnd || ' - '}`;
            default: return '';
        }
    }

    public isMatch(fieldVal: FilterFieldValue, value: any) {
        const filterValue = fieldVal.value as FilterDateRangeValue;

        if (!filterValue) {
            return false;
        }

        if (!filterValue.dateBegin && !filterValue.dateEnd) {
            return false;
        }

        const date = this.formatDateTime(value);
        return (!filterValue.dateBegin || (date >= filterValue.dateBegin)) && (!filterValue.dateEnd || (date <= filterValue.dateEnd));
    }

    public setValue(fieldVal: FilterFieldValue) {
        const rangeResolver = this.dynamicRanges[fieldVal.queryParamValue];
        if (rangeResolver) {
            fieldVal.value = rangeResolver();
        } else {
            fieldVal.value = {
                rangeType: 'dateRange',
                dateBegin: this.getDateBegin(fieldVal.queryParamValue),
                dateEnd: this.getDateEnd(fieldVal.queryParamValue)
            } as FilterDateRangeValue;
        }
        this.changes.next(true);
    }

    public setQueryParamValue(fieldVal: FilterFieldValue) {
        const filterValue = fieldVal.value as FilterDateRangeValue;

        if (!filterValue) {
            return;
        }

        if (filterValue.rangeType === 'dateRange') {
            fieldVal.queryParamValue = (filterValue.dateBegin || filterValue.dateEnd) ?
                `${(filterValue.dateBegin ? filterValue.dateBegin : '')}${this.separator}${(filterValue.dateEnd ? filterValue.dateEnd : '')}` : null;
        } else {
            fieldVal.queryParamValue = filterValue.rangeType;
        }
    }

    private getDateBegin(value: string): string {
        if (!value) {
            return null;
        }

        const match = this.dateBeginRegExp.exec(value);
        return match ? match[1] : '';
    }

    private getDateEnd(value: string): string {
        if (!value) {
            return null;
        }
        const match = this.dateEndRegExp.exec(value);
        return match ? match[1] : '';
    }

    private getTodayRange(): FilterDateRangeValue {
        return {
            dateBegin: this.formatDateTime(moment()),
            dateEnd: this.formatDateTime(moment()),
            rangeType: 'today'
        };
    }

    private getMonthToDateRange(): FilterDateRangeValue {
        return {
            dateBegin: this.formatDateTime(moment().startOf('month')),
            dateEnd: this.formatDateTime(moment()),
            rangeType: 'monthToDate'
        };
    }

    private getYearToDateRange(): FilterDateRangeValue {
        return {
            dateBegin: this.formatDateTime(moment().startOf('year')),
            dateEnd: this.formatDateTime(moment()),
            rangeType: 'yearToDate'
        };
    }
}